import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, TextField, Button, List, ListItem, ListItemText, IconButton, Stack, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const Alerts: React.FC = () => {
  const { authenticated, keycloak } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [topic, setTopic] = useState<'bill'|'member'|'tag'|'committee'>('bill');
  const [target, setTarget] = useState<string>('');
  const [event, setEvent] = useState<'status'|'hearing'|'vote'>('status');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (keycloak?.token) apiService.setToken(keycloak.token);
      setLoading(true);
      setError(null);
      try {
        const resp = await apiService.listAlerts();
        if (cancelled) return;
        setItems(Array.isArray(resp.data?.items) ? resp.data.items : (Array.isArray(resp.data?.alerts) ? resp.data.alerts : []));
      } catch (e: any) {
        setError('Failed to load alerts');
      } finally {
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [authenticated, keycloak?.token]);

  const handleCreate = async () => {
    try {
      setError(null);
      const payload = { topic, target: target || null, event } as any;
      const resp = await apiService.createAlert(payload);
      const created = resp?.data?.alert || resp?.data;
      if (!created?.id) throw new Error('Missing created record');
      setItems(prev => [{ ...payload, ...created }, ...prev]);
      setError(null);
      setTarget('');
    } catch (e: any) {
      setError('Failed to create alert');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiService.deleteAlert(id);
      setItems(items.filter((x) => x.id !== id));
    } catch {}
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Alerts</Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom>Create an alert</Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="topic-label">Topic</InputLabel>
            <Select labelId="topic-label" value={topic} label="Topic" onChange={(e) => setTopic(e.target.value as any)}>
              <MenuItem value="bill">Bill</MenuItem>
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="tag">Tag</MenuItem>
              <MenuItem value="committee">Committee</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Target (ID or slug)" value={target} onChange={(e) => setTarget(e.target.value)} size="small" sx={{ minWidth: 240 }} />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="event-label">Event</InputLabel>
            <Select labelId="event-label" value={event} label="Event" onChange={(e) => setEvent(e.target.value as any)}>
              <MenuItem value="status">Status</MenuItem>
              <MenuItem value="hearing">Hearing</MenuItem>
              <MenuItem value="vote">Vote</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </Stack>
      </Paper>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>My alerts</Typography>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <List>
            {items.map((a: any) => (
              <ListItem key={a.id} secondaryAction={<IconButton edge="end" aria-label="delete" onClick={() => handleDelete(a.id)}><DeleteIcon /></IconButton>}>
                <ListItemText primary={`${a.topic} • ${a.event}`} secondary={a.target || '(all)'} />
              </ListItem>
            ))}
            {items.length === 0 && <Typography variant="body2">No alerts yet.</Typography>}
          </List>
        )}
      </Paper>
    </Box>
  );
};

export default Alerts;
